<template>
  <section class="target-contact-import">
    <div class="d-flex align-items-center mb-3">
      <div class="counter">
        <span class="h5 font-weight-bold text-primary mr-1">{{ total }}</span>
        <span>{{ $t("lang.label.contacts") }}</span>
      </div>
    </div>

    <LoadingSpinner v-if="loadingContacts && total === 0 && !importing" />

    <!-- Empty state -->
    <div
      v-else-if="showEmptyState"
      class="empty-wrap text-center border rounded py-5 px-3"
    >
      <div class="empty-icon mx-auto mb-3">
        <i class="icon-users"></i>
      </div>
      <p class="h6 font-weight-bold mb-2">{{ $t("lang.orders.activation_import_empty_title") }}</p>
      <p class="text-muted small mb-4 mx-auto empty-sub">
        {{ $t("lang.orders.activation_import_empty_sub") }}
      </p>
      <Tooltip :text="importDisabledTooltip" :width="'auto'">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="disabled || !targetId || !canImportContacts"
          :style="!canImportContacts ? { pointerEvents: 'none' } : null"
          @click="openImportModal"
        >
          <i class="icon-upload mr-1"></i>
          {{ $t("lang.orders.activation_import_button") }}
        </button>
      </Tooltip>
    </div>

    <!-- List state -->
    <div v-else>
      <div v-if="sequenceActive" class="readonly-banner mb-3">
        <i class="icon-lock mr-2"></i>
        <div>
          <strong>{{ $t("lang.orders.activation_import_locked_title") }}</strong>
          <div class="small">
            {{ $t("lang.orders.activation_import_readonly_banner") }}
          </div>
        </div>
      </div>

      <div v-if="importing" class="import-banner mb-3">
        <div class="import-banner__spin"></div>
        <div class="import-banner__text">
          <strong>{{ $t("lang.orders.activation_import_in_progress") }}</strong>
          <div class="small text-muted">
            {{ $t("lang.orders.activation_import_in_progress_sub") }}
          </div>
        </div>
        <div class="import-banner__count">{{ importProgress }} / {{ importTotal }}</div>
      </div>

      <div
        v-if="showEnrichmentBanner"
        class="enrichment-banner mb-3"
        :class="enrichmentBannerClass"
      >
        <div v-if="enrichmentInProgress" class="enrichment-banner__spin"></div>
        <i v-else class="enrichment-banner__icon" :class="enrichmentBannerIcon"></i>
        <div class="enrichment-banner__content">
          <strong>{{ enrichmentBannerTitle }}</strong>
          <div class="small">{{ enrichmentBannerBody }}</div>
          <div
            v-if="showEnrichmentResults"
            class="enrichment-results mt-3"
          >
            <div class="enrichment-result">
              <strong>{{ enrichmentContactsEnriched }}</strong>
              <span>{{ $t("lang.orders.activation_enrichment_result_contacts") }}</span>
            </div>
            <div class="enrichment-result">
              <strong>{{ enrichmentEmailsFound }}</strong>
              <span>{{ $t("lang.orders.activation_enrichment_result_emails") }}</span>
            </div>
            <div class="enrichment-result">
              <strong>{{ enrichmentPhonesFound }}</strong>
              <span>{{ $t("lang.orders.activation_enrichment_result_phones") }}</span>
            </div>
            <div class="enrichment-result enrichment-result--removed">
              <strong>{{ enrichmentContactsRemoved }}</strong>
              <span>{{ $t("lang.orders.activation_enrichment_result_removed") }}</span>
            </div>
          </div>
          <div
            v-if="enrichmentInProgress && enrichmentTotal > 0"
            class="enrichment-banner__progress mt-2"
          >
            <span :style="{ width: enrichmentProgressPercent + '%' }"></span>
          </div>
        </div>
        <div v-if="enrichmentTotal > 0" class="enrichment-banner__count">
          {{ enrichmentProcessed }} / {{ enrichmentTotal }}
        </div>
        <button
          v-if="!enrichmentInProgress"
          type="button"
          class="btn btn-sm border-0 enrichment-banner__close"
          :title="$t('lang.button.close')"
          @click="dismissEnrichmentBanner"
        >
          <i class="icon-close"></i>
        </button>
      </div>

      <div class="list-toolbar d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div class="d-flex flex-wrap align-items-center">
          <span v-if="importFileName" class="filename badge badge-light border mr-2 mb-2">
            <i class="icon-doc mr-1"></i>{{ importFileName }}
          </span>
          <div class="search-box mb-2">
            <i class="icon-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control form-control-sm"
              :placeholder="$t('lang.orders.activation_import_search_placeholder')"
              @input="onSearchChange"
            />
          </div>
        </div>
        <div v-if="canManageImportList" class="d-flex align-items-center mb-2">
          <div class="actions-panel dropdown" :class="{ show: actionsOpen, open: actionsOpen }">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm dropdown-toggle"
              :disabled="disabled && !sequenceActive"
              @click.stop="actionsOpen = !actionsOpen"
            >
              {{ $t("lang.orders.activation_import_actions") }}
            </button>
            <div class="dropdown-menu dropdown-menu-right" :class="{ show: actionsOpen }">
              <div class="dropdown-header">
                {{
                  hasSelection
                    ? $t("lang.orders.activation_import_selected_count", {
                        count: selectedCount,
                      })
                    : $t("lang.orders.activation_import_no_selection")
                }}
              </div>
              <button
                type="button"
                class="dropdown-item"
                :disabled="!hasSelection || disabled || sequenceActive"
                @click="openEnrichModal"
              >
                {{ $t("lang.orders.activation_import_enrich_selection") }}
              </button>
              <button
                type="button"
                class="dropdown-item"
                :disabled="!hasSelection"
                @click="downloadSelection"
              >
                {{ $t("lang.orders.activation_import_download_selection") }}
              </button>
              <button
                type="button"
                class="dropdown-item text-danger"
                :disabled="!hasSelection || disabled || sequenceActive"
                @click="askDeleteSelected"
              >
                {{ $t("lang.orders.activation_import_delete_selection") }}
              </button>
            </div>
          </div>
          <Tooltip :text="importDisabledTooltip" :width="'auto'">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm ml-2"
              :disabled="disabled || !canImportContacts"
              :style="!canImportContacts ? { pointerEvents: 'none' } : null"
              @click="openAddContacts"
            >
              <i class="icon-plus mr-1"></i>
              {{ $t("lang.orders.activation_import_add_contacts") }}
            </button>
          </Tooltip>
        </div>
      </div>

      <div v-if="loadingContacts" class="list-loading py-4">
        <LoadingSpinner />
      </div>

      <template v-else>
      <div class="table-responsive border rounded">
        <table class="table table-sm table-hover mb-0 activation-table">
          <thead>
            <tr>
              <th class="col-check">
                <div class="selection-panel dropdown" :class="{ show: selectionMenuOpen, open: selectionMenuOpen }">
                  <button
                    type="button"
                    class="btn btn-sm btn-white selection-toggle border"
                    :class="{ 'selection-toggle--active': selectAllPages || somePageSelected || allPageSelected }"
                    :disabled="isSelectionDisabled"
                    @click.stop="selectionMenuOpen = !selectionMenuOpen"
                  >
                    <i
                      v-if="selectAllPages && excludedIds.length === 0"
                      class="text-primary icon-check-circle"
                    ></i>
                    <i
                      v-else-if="selectAllPages || somePageSelected || allPageSelected"
                      class="text-primary icon-check-circle"
                    ></i>
                    <i v-else class="icon-circle-o"></i>
                  </button>
                  <div class="dropdown-menu dropdown-menu-left" :class="{ show: selectionMenuOpen }">
                    <button
                      type="button"
                      class="dropdown-item"
                      :class="{ 'text-primary': selectAllPages }"
                      @click="toggleAllPagesMode"
                    >
                      <small>{{ $t("lang.mycontacts.selectallpages") }}</small>
                      <i v-if="selectAllPages" class="text-primary icon-checkmark ml-1"></i>
                    </button>
                    <button
                      type="button"
                      class="dropdown-item"
                      :class="{ 'text-primary': !selectAllPages && allPageSelected }"
                      @click="toggleOnlyCurrentPage"
                    >
                      <small>{{ $t("lang.mycontacts.selectonlypages") }}</small>
                      <i
                        v-if="!selectAllPages && allPageSelected"
                        class="text-primary icon-checkmark ml-1"
                      ></i>
                    </button>
                  </div>
                </div>
              </th>
              <th>
                {{ $t("lang.column.firstname") }}
                <span class="text-danger">*</span>
              </th>
              <th>
                {{ $t("lang.column.lastname") }}
                <span class="text-danger">*</span>
              </th>
              <th>
                {{ $t("lang.orders.activation_import_col_domain") }}
                <span class="text-danger">*</span>
              </th>
              <th>{{ $t("lang.column.jobtitle") }}</th>
              <th>{{ $t("lang.column.company") }}</th>
              <th>LinkedIn</th>
              <th>{{ $t("lang.label.email") }}</th>
              <th>{{ $t("lang.column.phone") }}</th>
              <th class="col-act">{{ $t("lang.orders.activation_import_col_actions") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="contact in contacts"
              :key="contact.id"
              :class="{ 'table-primary': isRowSelected(contact.id) }"
            >
              <td class="col-check">
                <input
                  type="checkbox"
                  :checked="isRowSelected(contact.id)"
                  :disabled="isSelectionDisabled"
                  @change="toggleOne(contact.id)"
                />
              </td>
              <td>{{ contact.first_name }}</td>
              <td>{{ contact.last_name }}</td>
              <td>{{ contact.domain }}</td>
              <td>{{ displayCell(contact.job) }}</td>
              <td>{{ displayCell(contact.company) }}</td>
              <td class="linkedin-cell text-center">
                <span
                  v-if="hasLinkedIn(contact)"
                  class="icon-check1 text-success linkedin-indicator"
                  :title="contact.linkedin"
                ></span>
                <span v-else class="text-muted empty-cell">{{
                  $t("lang.orders.activation_import_empty_cell")
                }}</span>
              </td>
              <td>
                <span
                  v-if="isContactFieldEnrichmentActive(contact, 'email')"
                  class="field-enrichment field-enrichment--active"
                  :title="contactFieldEnrichmentTitle(contact, 'email')"
                >
                  <span class="field-enrichment__spin"></span>
                  {{ $t("lang.orders.activation_enrichment_email_processing") }}
                </span>
                <span
                  v-else-if="isContactFieldEnrichmentFailed(contact, 'email')"
                  class="field-enrichment field-enrichment--failed"
                  :title="contactFieldEnrichmentTitle(contact, 'email')"
                >
                  <i class="icon-warning mr-1"></i>{{ displayCell(contact.email) }}
                </span>
                <span
                  v-else-if="isContactFieldEnrichmentNotFound(contact, 'email')"
                  class="field-enrichment field-enrichment--empty"
                  :title="$t('lang.orders.activation_enrichment_not_found')"
                >
                  <i class="icon-info mr-1"></i>{{ displayCell(contact.email) }}
                </span>
                <span v-else>{{ displayCell(contact.email) }}</span>
              </td>
              <td>
                <span
                  v-if="isContactFieldEnrichmentActive(contact, 'phone')"
                  class="field-enrichment field-enrichment--active"
                  :title="contactFieldEnrichmentTitle(contact, 'phone')"
                >
                  <span class="field-enrichment__spin"></span>
                  {{ $t("lang.orders.activation_enrichment_phone_processing") }}
                </span>
                <span
                  v-else-if="isContactFieldEnrichmentFailed(contact, 'phone')"
                  class="field-enrichment field-enrichment--failed"
                  :title="contactFieldEnrichmentTitle(contact, 'phone')"
                >
                  <i class="icon-warning mr-1"></i>{{ displayCell(contact.phone) }}
                </span>
                <span
                  v-else-if="isContactFieldEnrichmentNotFound(contact, 'phone')"
                  class="field-enrichment field-enrichment--empty"
                  :title="$t('lang.orders.activation_enrichment_not_found')"
                >
                  <i class="icon-info mr-1"></i>{{ displayCell(contact.phone) }}
                </span>
                <span v-else>{{ displayCell(contact.phone) }}</span>
              </td>
              <td class="col-act text-right">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger border-0"
                  :disabled="disabled || sequenceActive"
                  :title="
                    sequenceActive
                      ? $t('lang.orders.activation_import_locked_title')
                      : $t('lang.general.delete')
                  "
                  @click="askDeleteOne(contact.id)"
                >
                  <i class="icon-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="contacts.length === 0">
              <td colspan="10" class="text-center text-muted py-4">
                {{ $t("lang.label.noresultsfound") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="total > 0" class="row mt-3">
        <div class="col">
          <div class="float-left">
            <Pagination
              :total="total"
              :totalpage="contactsQty"
              :model="pagination"
              :limit="false"
              color="primary"
              @changePage="loadContacts"
              key="activation-pagination"
            />
          </div>
        </div>
      </div>
      </template>
    </div>

    <!-- Import modal -->
    <div v-if="showImportModal" class="tci-overlay" @click.self="closeImportModal">
      <div class="tci-modal card shadow">
        <div class="card-header d-flex align-items-center justify-content-between">
          <strong>{{ importModalTitle }}</strong>
          <button type="button" class="btn btn-sm btn-light border-0" @click="closeImportModal">
            <i class="icon-close"></i>
          </button>
        </div>
        <div class="card-body">
          <div class="steps d-flex align-items-center mb-4">
            <div class="step" :class="stepClass(1)">
              <span class="step-num">1</span>
              {{ $t("lang.orders.activation_import_step_file") }}
            </div>
            <div class="step-sep flex-fill mx-2"></div>
            <div class="step" :class="stepClass(2)">
              <span class="step-num">2</span>
              {{ $t("lang.orders.activation_import_step_mapping") }}
            </div>
            <div class="step-sep flex-fill mx-2"></div>
            <div class="step" :class="stepClass(3)">
              <span class="step-num">3</span>
              {{ $t("lang.orders.activation_import_step_preview") }}
            </div>
          </div>

          <div v-if="importStep === 1">
            <div
              class="dropzone"
              :class="{ 'dropzone--active': dragOver }"
              @click="triggerFileInput"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onFileDrop"
            >
              <input
                ref="fileInput"
                type="file"
                class="d-none"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                @change="onFileSelected"
              />
              <div class="dropzone-icon"><i class="icon-upload"></i></div>
              <div class="font-weight-bold">{{ $t("lang.orders.activation_import_dropzone_main") }}</div>
              <div class="text-muted small">{{ $t("lang.orders.activation_import_dropzone_sub") }}</div>
              <a
                href="./files/oliver-activation-import-template.csv"
                class="small font-weight-bold mt-3 d-inline-block"
                @click.stop
              >
                {{ $t("lang.orders.activation_import_download_template") }}
              </a>
            </div>
            <div v-if="pickedFile" class="file-picked mt-3 p-3 rounded d-flex align-items-center">
              <i class="icon-doc text-success mr-2"></i>
              <div>
                <div class="font-weight-bold">{{ pickedFile.name }}</div>
                <div class="small text-muted">
                  {{
                    $t("lang.orders.activation_import_file_meta", {
                      rows: rawRows.length,
                      cols: fileColumns.length,
                    })
                  }}
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="importStep === 2">
            <p class="text-muted small mb-3">{{ $t("lang.orders.activation_import_map_intro") }}</p>
            <div
              v-for="field in targetFields"
              :key="field.key"
              class="map-row d-flex align-items-center py-2 border-bottom"
            >
              <div class="map-target flex-fill font-weight-bold">
                {{ field.label }}
                <span v-if="field.required" class="text-danger">*</span>
              </div>
              <div class="mx-2 text-muted">→</div>
              <select
                v-model="columnMapping[field.key]"
                class="form-control form-control-sm flex-fill"
                :class="{ 'text-muted': !columnMapping[field.key] }"
              >
                <option value="">{{ $t("lang.orders.activation_import_do_not_import") }}</option>
                <option v-for="col in fileColumns" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
          </div>

          <div v-else-if="importStep === 3">
            <p class="text-muted small mb-3">{{ $t("lang.orders.activation_import_preview_intro") }}</p>

            <div
              v-for="(warning, index) in mappingWarnings"
              :key="'map-warning-' + index"
              class="alert alert-warning small mb-2"
            >
              {{ warning }}
            </div>

            <div class="preview-mapping mb-3 p-3 rounded">
              <div class="small font-weight-bold mb-2">
                {{ $t("lang.orders.activation_import_preview_mapping_title") }}
              </div>
              <div
                v-for="item in mappingSummary"
                :key="item.key"
                class="preview-mapping-row d-flex align-items-center py-1 small"
              >
                <span class="preview-mapping-label">
                  {{ item.label }}
                  <span v-if="item.required" class="text-danger">*</span>
                </span>
                <span class="mx-2 text-muted">→</span>
                <span class="font-weight-bold">{{ item.column }}</span>
              </div>
            </div>

            <div class="preview-stats d-flex flex-wrap mb-3">
              <span class="badge badge-success mr-2 mb-1">
                {{
                  $t("lang.orders.activation_import_preview_valid_rows", {
                    count: validPreviewRows.length,
                  })
                }}
              </span>
              <span v-if="invalidPreviewCount > 0" class="badge badge-warning mb-1">
                {{
                  $t("lang.orders.activation_import_preview_invalid_rows", {
                    count: invalidPreviewCount,
                  })
                }}
              </span>
            </div>

            <div class="small font-weight-bold mb-2">
              {{ $t("lang.orders.activation_import_preview_sample") }}
            </div>
            <div class="table-responsive border rounded preview-table-wrap">
              <table class="table table-sm mb-0 activation-table">
                <thead>
                  <tr>
                    <th>{{ $t("lang.column.firstname") }}</th>
                    <th>{{ $t("lang.column.lastname") }}</th>
                    <th>{{ $t("lang.orders.activation_import_col_domain") }}</th>
                    <th>{{ $t("lang.label.email") }}</th>
                    <th>{{ $t("lang.column.company") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in previewSampleRows" :key="'preview-' + index">
                    <td>{{ row.first_name }}</td>
                    <td>{{ row.last_name }}</td>
                    <td>{{ row.domain }}</td>
                    <td>{{ row.email }}</td>
                    <td>{{ displayCell(row.company) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="validPreviewRows.length > previewSampleRows.length" class="text-muted small mt-2 mb-0">
              {{
                $t("lang.orders.activation_import_preview_more_rows", {
                  count: validPreviewRows.length - previewSampleRows.length,
                })
              }}
            </p>
          </div>
          <div v-if="fileError" class="alert alert-danger mt-3 mb-0 small">{{ fileError }}</div>
        </div>
        <div class="card-footer d-flex justify-content-between">
          <button
            v-if="importStep > 1"
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="goImportStepBack"
          >
            {{ $t("lang.button.back") }}
          </button>
          <span v-else></span>
          <div>
            <button type="button" class="btn btn-outline-secondary btn-sm mr-2" @click="closeImportModal">
              {{ $t("lang.button.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!canContinueImport"
              @click="onImportContinue"
            >
              {{ importContinueLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Import launched -->
    <div v-if="showLaunchedModal" class="tci-overlay" @click.self="showLaunchedModal = false">
      <div class="tci-modal tci-modal--sm card shadow">
        <div class="card-header d-flex align-items-center justify-content-between">
          <strong>{{ $t("lang.orders.activation_import_launched_title") }}</strong>
          <button type="button" class="btn btn-sm btn-light border-0" @click="showLaunchedModal = false">
            <i class="icon-close"></i>
          </button>
        </div>
        <div class="card-body p-4 text-center">
          <div class="launch-icon mx-auto mb-3"><i class="icon-upload"></i></div>
          <h5 class="font-weight-bold">{{ $t("lang.orders.activation_import_launched_heading") }}</h5>
          <p class="text-muted small mb-0">{{ $t("lang.orders.activation_import_launched_body") }}</p>
          <p
            v-if="importUpdated > 0"
            class="text-muted small mb-0 mt-2"
          >
            {{
              $t("lang.orders.activation_import_updated_count", {
                count: importUpdated,
              })
            }}
          </p>
          <p
            v-if="importAlreadyKnown > 0"
            class="text-muted small mb-0 mt-2"
          >
            {{
              $t("lang.orders.activation_import_already_known_count", {
                count: importAlreadyKnown,
              })
            }}
          </p>
          <p
            v-if="importDuplicatesIgnored > 0"
            class="text-muted small mb-0 mt-2"
          >
            {{
              $t("lang.orders.activation_import_duplicates_ignored", {
                count: importDuplicatesIgnored,
              })
            }}
          </p>
        </div>
        <div class="card-footer justify-content-center">
          <button type="button" class="btn btn-primary btn-sm" @click="showLaunchedModal = false">
            {{ $t("lang.orders.activation_import_launched_cta") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sequence locked -->
    <div v-if="showLockedModal" class="tci-overlay" @click.self="showLockedModal = false">
      <div class="tci-modal tci-modal--sm card shadow">
        <div class="card-header d-flex align-items-center justify-content-between">
          <strong>{{ $t("lang.orders.activation_import_locked_modal_title") }}</strong>
          <button type="button" class="btn btn-sm btn-light border-0" @click="showLockedModal = false">
            <i class="icon-close"></i>
          </button>
        </div>
        <div class="card-body p-4 text-center">
          <div class="warn-icon mx-auto mb-3"><i class="icon-lock"></i></div>
          <h5 class="font-weight-bold">{{ $t("lang.orders.activation_import_locked_title") }}</h5>
          <p class="text-muted small mb-0">{{ $t("lang.orders.activation_import_locked_body") }}</p>
        </div>
        <div class="card-footer justify-content-center">
          <button type="button" class="btn btn-primary btn-sm" @click="showLockedModal = false">
            {{ $t("lang.button.ok") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirm delete -->
    <div v-if="showConfirmModal" class="tci-overlay" @click.self="showConfirmModal = false">
      <div class="tci-modal tci-modal--sm card shadow">
        <div class="card-header d-flex align-items-center justify-content-between">
          <strong>{{ $t("lang.orders.activation_import_confirm_delete_title") }}</strong>
          <button type="button" class="btn btn-sm btn-light border-0" @click="showConfirmModal = false">
            <i class="icon-close"></i>
          </button>
        </div>
        <div class="card-body p-4 text-center">
          <div class="warn-icon warn-icon--danger mx-auto mb-3"><i class="icon-trash"></i></div>
          <h5 class="font-weight-bold">{{ $t("lang.orders.activation_import_confirm_heading") }}</h5>
          <p class="text-muted small mb-0">
            {{
              $t("lang.orders.activation_import_confirm_delete_body", {
                count: pendingDeleteIds.length,
              })
            }}
          </p>
        </div>
        <div class="card-footer justify-content-end">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm mr-2"
            @click="showConfirmModal = false"
          >
            {{ $t("lang.button.cancel") }}
          </button>
          <button type="button" class="btn btn-danger btn-sm" @click="confirmDelete">
            {{ $t("lang.general.delete") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Enrich selection -->
    <div v-if="showEnrichModal" class="tci-overlay" @click.self="closeEnrichModal">
      <div class="tci-modal tci-modal--sm card shadow">
        <div class="card-header d-flex align-items-center justify-content-between">
          <strong>{{ $t("lang.orders.activation_import_enrich_title") }}</strong>
          <button type="button" class="btn btn-sm btn-light border-0" @click="closeEnrichModal">
            <i class="icon-close"></i>
          </button>
        </div>
        <div class="card-body p-4">
          <p class="small text-muted mb-3">
            {{
              $t("lang.orders.activation_import_enrich_body", {
                count: selectedCount,
              })
            }}
          </p>
          <div class="enrich-option d-flex align-items-center justify-content-between py-2 border-bottom">
            <label class="mb-0 d-flex align-items-center">
              <input type="checkbox" class="mr-2" :checked="true" disabled />
              {{ $t("lang.label.email") }}
              <span class="badge badge-primary ml-2">{{
                $t("lang.orders.activation_import_enrich_required")
              }}</span>
            </label>
          </div>
          <div class="enrich-option d-flex align-items-center justify-content-between py-2">
            <label class="mb-0 d-flex align-items-center">
              <input v-model="enrichIncludePhone" type="checkbox" class="mr-2" />
              {{ $t("lang.orders.activation_import_enrich_phone") }}
            </label>
          </div>
        </div>
        <div class="card-footer justify-content-end">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm mr-2"
            @click="closeEnrichModal"
          >
            {{ $t("lang.button.cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="enriching"
            @click="confirmEnrich"
          >
            {{ $t("lang.orders.activation_import_enrich_cta") }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import * as XLSX from "xlsx";
import { mapState } from "vuex";
import { targetsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Pagination from "@/components/Common/Pagination.vue";
import Tooltip from "@/components/Common/Tooltip.vue";

const MAX_IMPORT_ROWS = 10000;
const EMAIL_SHAPE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Aliases are ordered by preference: the first alias that matches a file column
// wins (e.g. "Email (FullEnrich)" is picked before "All Valid Emails (FullEnrich)",
// "Domain (FullEnrich)" before "Website"). Headers are normalized first
// (lowercase, accents removed, "_"/"-" as spaces, parenthesized suffix ignored).
const GUESS_ALIASES = {
  first_name: ["prénom", "prenom", "firstname", "first name", "nombre"],
  last_name: ["nom", "lastname", "last name", "apellido"],
  domain: ["domaine", "domain", "website", "site web", "sitio web"],
  post: ["poste", "job title", "jobtitle", "job", "titre", "cargo"],
  company: ["entreprise", "company name", "company", "société", "societe", "empresa"],
  linkedin: ["linkedin url", "url linkedin", "linkedin profile url", "linkedin"],
  email: ["email", "e-mail", "mail", "correo", "all valid emails"],
  phone: ["phone number", "téléphone", "telephone", "phone", "tel", "mobile", "teléfono"],
};

export default {
  components: { LoadingSpinner, Pagination, Tooltip },
  props: {
    targetId: {
      type: [Number, String],
      default: null,
    },
    targetStatusId: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      contacts: [],
      total: 0,
      withEmailCount: 0,
      withoutEmailCount: 0,
      selectedIds: [],
      selectAllPages: false,
      excludedIds: [],
      selectionMenuOpen: false,
      searchQuery: "",
      lastSearchQuery: null,
      searchDebounceTimer: null,
      pagination: {
        currentPage: 1,
        cantPerPage: 25,
      },
      importFileName: "",
      actionsOpen: false,
      showImportModal: false,
      showLaunchedModal: false,
      showLockedModal: false,
      showConfirmModal: false,
      showEnrichModal: false,
      enrichIncludePhone: false,
      enriching: false,
      enrichmentStatus: null,
      enrichmentStatusRequestInFlight: false,
      enrichmentPollTimer: null,
      enrichmentWasInProgress: false,
      showEnrichmentTerminalBanner: false,
      pendingDeleteIds: [],
      selectedContactsById: {},
      importMode: "initial",
      importStep: 1,
      dragOver: false,
      pickedFile: null,
      rawRows: [],
      fileColumns: [],
      columnMapping: {},
      fileError: "",
      importing: false,
      importProgress: 0,
      importTotal: 0,
      importDuplicatesIgnored: 0,
      importUpdated: 0,
      importAlreadyKnown: 0,
      loadingContacts: false,
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.authentication.user,
    }),
    canManageImportList() {
      return Number(this.currentUser && this.currentUser.role_id) !== 4;
    },
    sequenceActive() {
      return Number(this.targetStatusId) === 4;
    },
    canImportContacts() {
      return Number(this.targetStatusId) === 3;
    },
    importDisabledTooltip() {
      return this.canImportContacts
        ? ""
        : this.$t("lang.orders.activation_import_draft_only_body");
    },
    enrichmentInProgress() {
      return !!(this.enrichmentStatus && this.enrichmentStatus.in_progress);
    },
    showEnrichmentBanner() {
      return !!this.enrichmentStatus
        && (this.enrichmentInProgress || this.showEnrichmentTerminalBanner);
    },
    enrichmentStatusCode() {
      if (!this.enrichmentStatus) return "";
      const status = this.enrichmentStatus.salesridge_batch_status
        || this.enrichmentStatus.status
        || "";
      return status === "skipped" && this.enrichmentStatus.status === "completed"
        ? "completed"
        : status;
    },
    enrichmentProcessed() {
      return Number(this.enrichmentStatus && this.enrichmentStatus.processed) || 0;
    },
    enrichmentTotal() {
      return Number(this.enrichmentStatus && this.enrichmentStatus.total) || 0;
    },
    enrichmentProgressPercent() {
      if (this.enrichmentTotal <= 0) return 0;
      return Math.min(100, Math.round((this.enrichmentProcessed / this.enrichmentTotal) * 100));
    },
    showEnrichmentResults() {
      return !this.enrichmentInProgress && this.enrichmentStatusCode === "completed";
    },
    enrichmentContactsEnriched() {
      return Number(this.enrichmentStatus && this.enrichmentStatus.contacts_enriched) || 0;
    },
    enrichmentEmailsFound() {
      return Number(this.enrichmentStatus && this.enrichmentStatus.emails_found) || 0;
    },
    enrichmentPhonesFound() {
      return Number(this.enrichmentStatus && this.enrichmentStatus.phones_found) || 0;
    },
    enrichmentContactsRemoved() {
      return Number(this.enrichmentStatus && this.enrichmentStatus.contacts_removed) || 0;
    },
    enrichmentBannerClass() {
      if (this.enrichmentInProgress) {
        return this.enrichmentStatusCode === "retry_pending"
          ? "enrichment-banner--retry"
          : "enrichment-banner--processing";
      }
      if (this.enrichmentStatusCode === "completed") return "enrichment-banner--completed";
      if (this.enrichmentStatusCode === "expired") return "enrichment-banner--expired";
      return "enrichment-banner--failed";
    },
    enrichmentBannerIcon() {
      if (this.enrichmentStatusCode === "completed") return "icon-check1";
      if (this.enrichmentStatusCode === "expired") return "icon-clock";
      return "icon-warning";
    },
    enrichmentBannerTitle() {
      if (this.enrichmentInProgress) {
        return this.enrichmentStatusCode === "retry_pending"
          ? this.$t("lang.orders.activation_enrichment_retry_title")
          : this.$t("lang.orders.activation_enrichment_processing_title");
      }
      if (this.enrichmentStatusCode === "completed") {
        return this.$t("lang.orders.activation_enrichment_completed_title");
      }
      if (this.enrichmentStatusCode === "expired") {
        return this.$t("lang.orders.activation_enrichment_expired_title");
      }
      return this.$t("lang.orders.activation_enrichment_failed_title");
    },
    enrichmentBannerBody() {
      if (this.enrichmentInProgress) {
        return this.enrichmentStatusCode === "retry_pending"
          ? this.$t("lang.orders.activation_enrichment_retry_body")
          : this.$t("lang.orders.activation_enrichment_processing_body");
      }
      if (this.enrichmentStatusCode === "completed") {
        return this.$t("lang.orders.activation_enrichment_completed_body");
      }
      if (this.enrichmentStatusCode === "expired") {
        return this.$t("lang.orders.activation_enrichment_expired_body");
      }
      return this.$t("lang.orders.activation_enrichment_failed_body");
    },
    isSelectionDisabled() {
      return this.disabled && !this.sequenceActive;
    },
    targetFields() {
      return [
        { key: "first_name", label: this.$t("lang.column.firstname"), required: true },
        { key: "last_name", label: this.$t("lang.column.lastname"), required: true },
        { key: "domain", label: this.$t("lang.orders.activation_import_col_domain"), required: true },
        { key: "post", label: this.$t("lang.column.jobtitle"), required: false },
        { key: "company", label: this.$t("lang.column.company"), required: false },
        { key: "linkedin", label: "LinkedIn", required: false },
        { key: "email", label: this.$t("lang.label.email"), required: false },
        { key: "phone", label: this.$t("lang.column.phone"), required: false },
      ];
    },
    showEmptyState() {
      return (
        this.total === 0 &&
        !this.searchQuery.trim() &&
        !this.importing &&
        !this.loadingContacts
      );
    },
    contactsQty() {
      return this.contacts.length;
    },
    pageContactIds() {
      return this.contacts.map((c) => c.id);
    },
    allPageSelected() {
      if (this.selectAllPages) {
        return (
          this.pageContactIds.length > 0 &&
          this.pageContactIds.every((id) => !this.excludedIds.includes(id))
        );
      }
      return (
        this.pageContactIds.length > 0 &&
        this.pageContactIds.every((id) => this.selectedIds.includes(id))
      );
    },
    somePageSelected() {
      if (this.selectAllPages) {
        return (
          this.pageContactIds.length > 0 &&
          this.pageContactIds.some((id) => !this.excludedIds.includes(id))
        );
      }
      return this.pageContactIds.some((id) => this.selectedIds.includes(id));
    },
    hasSelection() {
      if (this.selectAllPages) {
        return this.total > this.excludedIds.length;
      }
      return this.selectedIds.length > 0;
    },
    selectedCount() {
      if (this.selectAllPages) {
        return Math.max(0, this.total - this.excludedIds.length);
      }
      return this.selectedIds.length;
    },
    canContinueImport() {
      if (this.importStep === 1) return !!this.pickedFile && this.rawRows.length > 0 && !this.fileError;
      if (this.importStep === 2) {
        return this.targetFields
          .filter((f) => f.required)
          .every((f) => !!this.columnMapping[f.key]);
      }
      return this.validPreviewRows.length > 0;
    },
    mappedPreviewRows() {
      if (!this.rawRows.length) return [];
      return this.rawRows.map((row) => this.mapRow(row));
    },
    emailColumnHasEmails() {
      const col = this.columnMapping.email;
      if (!col) return true;
      return this.rawRows.some((row) => EMAIL_SHAPE_RE.test(String(row[col] || "").trim()));
    },
    companyColumnLooksLikeEmail() {
      const col = this.columnMapping.company;
      if (!col) return false;
      let filled = 0;
      let withAt = 0;
      this.rawRows.forEach((row) => {
        const value = String(row[col] || "").trim();
        if (!value) return;
        filled++;
        if (value.includes("@")) withAt++;
      });
      return filled > 0 && withAt / filled > 0.5;
    },
    mappingWarnings() {
      const warnings = [];
      if (this.companyColumnLooksLikeEmail) {
        warnings.push(
          this.$t("lang.orders.activation_import_company_email_warning", {
            column: this.columnMapping.company,
          }),
        );
      }
      if (!this.columnMapping.email) {
        warnings.push(
          this.$t("lang.orders.activation_import_email_unmapped_warning", {
            count: this.validPreviewRows.length,
          }),
        );
      }
      return warnings;
    },
    validPreviewRows() {
      return this.mappedPreviewRows.filter((row) => this.isValidRow(row));
    },
    invalidPreviewCount() {
      return this.mappedPreviewRows.length - this.validPreviewRows.length;
    },
    previewSampleRows() {
      return this.validPreviewRows.slice(0, 8);
    },
    mappingSummary() {
      return this.targetFields
        .filter((field) => this.columnMapping[field.key])
        .map((field) => ({
          key: field.key,
          label: field.label,
          column: this.columnMapping[field.key],
          required: field.required,
        }));
    },
    importContinueLabel() {
      if (this.importStep === 1 || this.importStep === 2) {
        return this.$t("lang.button.continue");
      }
      return this.$t("lang.orders.activation_import_button");
    },
    importModalTitle() {
      if (this.importMode === "add") {
        return this.$t("lang.orders.activation_import_add_contacts");
      }
      return this.$t("lang.orders.activation_import_modal_title");
    },
  },
  watch: {
    targetId: {
      immediate: true,
      handler(value) {
        this.resetEnrichmentTracking();
        if (value) {
          this.pagination.currentPage = 1;
          this.loadContacts();
          this.refreshEnrichmentStatus();
        }
      },
    },
  },
  mounted() {
    document.addEventListener("click", this.closeActionsMenu);
    document.addEventListener("click", this.closeSelectionMenu);
    this.$emit("contacts-changed", this.getContactsStatsPayload());
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeActionsMenu);
    document.removeEventListener("click", this.closeSelectionMenu);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.clearEnrichmentPollTimer();
  },
  methods: {
    clearEnrichmentPollTimer() {
      if (this.enrichmentPollTimer) {
        clearTimeout(this.enrichmentPollTimer);
        this.enrichmentPollTimer = null;
      }
    },
    resetEnrichmentTracking() {
      this.clearEnrichmentPollTimer();
      this.enrichmentStatus = null;
      this.enrichmentStatusRequestInFlight = false;
      this.enrichmentWasInProgress = false;
      this.showEnrichmentTerminalBanner = false;
    },
    dismissEnrichmentBanner() {
      this.showEnrichmentTerminalBanner = false;
    },
    scheduleEnrichmentStatusPoll(delay = 5000) {
      this.clearEnrichmentPollTimer();
      if (!this.targetId) return;
      this.enrichmentPollTimer = setTimeout(() => {
        this.enrichmentPollTimer = null;
        this.refreshEnrichmentStatus();
      }, delay);
    },
    beginEnrichmentTracking(response) {
      const queued = Number(response && response.queued) || 0;
      this.showEnrichmentTerminalBanner = false;
      this.enrichmentWasInProgress = true;
      this.enrichmentStatus = {
        request_id: response && response.target_activation_enrichment_request_id,
        in_progress: true,
        status: "pending",
        salesridge_batch_status: (response && response.salesridge_batch_status) || "pending",
        processed: 0,
        total: queued,
      };
      this.scheduleEnrichmentStatusPoll(1000);
    },
    refreshEnrichmentStatus() {
      if (!this.targetId || this.enrichmentStatusRequestInFlight) {
        return Promise.resolve();
      }

      const requestedTargetId = String(this.targetId);
      const previousStatus = this.enrichmentStatus;
      this.enrichmentStatusRequestInFlight = true;

      return targetsService
        .getActivationImportStatus(this.targetId)
        .then((response) => {
          if (String(this.targetId) !== requestedTargetId) return;

          const wasInProgress = this.enrichmentWasInProgress
            || !!(previousStatus && previousStatus.in_progress);
          const requestChanged = !!previousStatus
            && String(previousStatus.request_id || "") !== String((response && response.request_id) || "");
          const progressChanged = !!previousStatus
            && Number(previousStatus.processed || 0) !== Number((response && response.processed) || 0);

          this.enrichmentStatus = response || null;

          if (response && response.in_progress) {
            this.enrichmentWasInProgress = true;
            this.showEnrichmentTerminalBanner = false;
            if (requestChanged || progressChanged) {
              this.loadContacts();
            }
            this.scheduleEnrichmentStatusPoll();
            return;
          }

          this.clearEnrichmentPollTimer();
          this.enrichmentWasInProgress = false;

          const terminalStatuses = ["completed", "failed", "expired"];
          const hasTerminalStatus = !!response
            && terminalStatuses.includes(response.status);
          this.showEnrichmentTerminalBanner = hasTerminalStatus;

          if (wasInProgress && hasTerminalStatus) {
            this.loadContacts({ fetchTotal: true });
          }
        })
        .catch(() => {
          if (String(this.targetId) === requestedTargetId && this.enrichmentWasInProgress) {
            this.scheduleEnrichmentStatusPoll(10000);
          }
        })
        .finally(() => {
          this.enrichmentStatusRequestInFlight = false;
        });
    },
    contactFieldEnrichmentStatus(contact, field) {
      if (!contact) return "";
      return contact[`${field}_enrichment_status`] || "";
    },
    isContactFieldEnrichmentActive(contact, field) {
      const activeStatuses = ["pending", "claimed", "processing", "retry_pending"];
      return Number(contact && contact.enrichment_in_progress) === 1
        && activeStatuses.includes(this.contactFieldEnrichmentStatus(contact, field));
    },
    isContactFieldEnrichmentFailed(contact, field) {
      return ["failed", "expired"].includes(this.contactFieldEnrichmentStatus(contact, field))
        && !contact[field];
    },
    isContactFieldEnrichmentNotFound(contact, field) {
      return this.contactFieldEnrichmentStatus(contact, field) === "not_found"
        && !contact[field];
    },
    contactFieldEnrichmentTitle(contact, field) {
      const status = this.contactFieldEnrichmentStatus(contact, field);
      if (status === "failed") {
        return this.$t("lang.orders.activation_enrichment_field_failed");
      }
      if (status === "expired") {
        return this.$t("lang.orders.activation_enrichment_field_expired");
      }
      if (contact && contact.salesridge_batch_status === "retry_pending") {
        return this.$t("lang.orders.activation_enrichment_field_retrying");
      }
      return field === "email"
        ? this.$t("lang.orders.activation_enrichment_email_processing")
        : this.$t("lang.orders.activation_enrichment_phone_processing");
    },
    displayCell(value) {
      if (value) return value;
      return this.$t("lang.orders.activation_import_empty_cell");
    },
    hasLinkedIn(contact) {
      return !!(contact && contact.linkedin && String(contact.linkedin).trim());
    },
    stepClass(step) {
      if (this.importStep === step) return "step--active";
      if (this.importStep > step) return "step--done";
      return "";
    },
    getContactsStatsPayload() {
      return {
        total: this.total,
        with_email: this.withEmailCount,
        without_email: this.withoutEmailCount,
      };
    },
    emitContactsChanged() {
      this.$emit("contacts-changed", this.getContactsStatsPayload());
    },
    closeActionsMenu() {
      this.actionsOpen = false;
    },
    closeSelectionMenu() {
      this.selectionMenuOpen = false;
    },
    isRowSelected(id) {
      if (this.selectAllPages) {
        return !this.excludedIds.includes(id);
      }
      return this.selectedIds.includes(id);
    },
    toggleAllPagesMode() {
      if (this.selectAllPages) {
        this.clearSelection();
      } else {
        this.selectAllPages = true;
        this.excludedIds = [];
        this.selectedIds = [];
      }
      this.selectionMenuOpen = false;
    },
    toggleOnlyCurrentPage() {
      if (!this.selectAllPages && this.allPageSelected) {
        this.clearSelection();
      } else {
        this.selectAllPages = false;
        this.excludedIds = [];
        this.selectedIds = [...this.pageContactIds];
        this.contacts.forEach((contact) => {
          this.$set(this.selectedContactsById, contact.id, contact);
        });
      }
      this.selectionMenuOpen = false;
    },
    clearSelection() {
      this.selectAllPages = false;
      this.excludedIds = [];
      this.selectedIds = [];
      this.selectedContactsById = {};
    },
    openImportModal(mode = "initial") {
      if (!this.canImportContacts) return;
      this.importMode = mode;
      this.resetImportState();
      this.showImportModal = true;
    },
    openAddContacts() {
      if (!this.canImportContacts) return;
      this.openImportModal("add");
    },
    closeImportModal() {
      this.showImportModal = false;
    },
    resetImportState() {
      this.importStep = 1;
      this.pickedFile = null;
      this.rawRows = [];
      this.fileColumns = [];
      this.columnMapping = {};
      this.fileError = "";
      this.dragOver = false;
    },
    triggerFileInput() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    onFileDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) this.processFile(file);
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) this.processFile(file);
      event.target.value = "";
    },
    processFile(file) {
      this.fileError = "";
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["csv", "xlsx", "xls"].includes(ext)) {
        this.fileError = this.$t("lang.orders.activation_import_invalid_format");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const buffer = e.target.result;
          const workbook = this.readWorkbook(buffer, ext);
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          if (!rows.length) {
            this.fileError = this.$t("lang.mycontacts.csvempty");
            return;
          }
          if (rows.length > MAX_IMPORT_ROWS) {
            this.fileError = this.$t("lang.orders.activation_import_max_rows", {
              max: MAX_IMPORT_ROWS,
            });
            return;
          }
          this.pickedFile = file;
          this.rawRows = rows;
          this.fileColumns = Object.keys(rows[0] || {});
          this.buildAutoMapping();
        } catch (err) {
          this.fileError = this.$t("lang.orders.activation_import_parse_error");
        }
      };
      reader.readAsArrayBuffer(file);
    },
    decodeCsvText(buffer) {
      let bytes = new Uint8Array(buffer);
      // UTF-8 BOM (common when exported from Excel)
      if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
        bytes = bytes.slice(3);
      }
      try {
        return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
      } catch (error) {
        // Fallback for legacy CSV saved as Windows-1252 / Latin-1
        return new TextDecoder("windows-1252").decode(bytes);
      }
    },
    readWorkbook(buffer, ext) {
      if (ext === "csv") {
        const text = this.decodeCsvText(buffer);
        return XLSX.read(text, { type: "string" });
      }
      return XLSX.read(new Uint8Array(buffer), { type: "array" });
    },
    normalizeHeader(value) {
      return String(value || "")
        .replace(/\s*\([^)]*\)/g, " ") // ignore provider suffixes: "Email (FullEnrich)" -> "Email"
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    buildAutoMapping() {
      const normalizedColumns = this.fileColumns.map((col) => ({
        col,
        normalized: this.normalizeHeader(col),
      }));
      const usedColumns = {};
      const mapping = {};
      this.targetFields.forEach((field) => {
        mapping[field.key] = "";
        const aliases = (GUESS_ALIASES[field.key] || []).map((a) => this.normalizeHeader(a));
        for (const alias of aliases) {
          const match = normalizedColumns.find(
            (entry) => entry.normalized === alias && !usedColumns[entry.col],
          );
          if (match) {
            mapping[field.key] = match.col;
            usedColumns[match.col] = true;
            break;
          }
        }
      });
      this.columnMapping = mapping;
    },
    onImportContinue() {
      if (this.importStep === 1) {
        this.importStep = 2;
        return;
      }
      if (this.importStep === 2) {
        if (this.validPreviewRows.length === 0) {
          this.fileError = this.$t("lang.orders.activation_import_no_valid_rows");
          return;
        }
        if (!this.emailColumnHasEmails) {
          this.fileError = this.$t("lang.orders.activation_import_email_column_invalid", {
            column: this.columnMapping.email,
          });
          return;
        }
        this.fileError = "";
        this.importStep = 3;
        return;
      }
      this.launchImport();
    },
    goImportStepBack() {
      this.fileError = "";
      if (this.importStep === 3) {
        this.importStep = 2;
        return;
      }
      if (this.importStep === 2) {
        this.importStep = 1;
      }
    },
    mapRow(row) {
      const mapped = {};
      this.targetFields.forEach((field) => {
        const col = this.columnMapping[field.key];
        mapped[field.key] = col ? String(row[col] || "").trim() : "";
      });
      mapped.job = mapped.post;
      if (!mapped.company && mapped.domain) {
        mapped.company = mapped.domain;
      }
      return mapped;
    },
    isValidRow(row) {
      return ["first_name", "last_name", "domain"].every((key) => !!row[key]);
    },
    launchImport() {
      const rows = this.validPreviewRows;
      if (!rows.length) {
        this.fileError = this.$t("lang.orders.activation_import_no_valid_rows");
        return;
      }
      this.fileError = "";
      this.closeImportModal();
      this.importFileName = this.pickedFile ? this.pickedFile.name : "";
      this.importTotal = rows.length;
      this.importProgress = 0;
      this.importing = true;
      this.runBulkImport(rows);
    },
    async runBulkImport(rows) {
      const IMPORT_BATCH_SIZE = 300;
      const contacts = rows.map((row) => ({
        first_name: row.first_name,
        last_name: row.last_name,
        domain: row.domain,
        email: row.email,
        company: row.company || row.domain,
        post: row.post,
        linkedin: row.linkedin,
        phone: row.phone,
      }));
      let imported = 0;
      let linked = 0;
      let updated = 0;
      let alreadyKnown = 0;
      let duplicatesIgnored = 0;

      try {
        for (let i = 0; i < contacts.length; i += IMPORT_BATCH_SIZE) {
          const batch = contacts.slice(i, i + IMPORT_BATCH_SIZE);
          const result = await targetsService.importActivationContacts(this.targetId, {
            file_name: this.importFileName,
            contacts: batch,
          });
          imported += Number(result && result.imported) || 0;
          linked += Number(result && result.linked) || 0;
          updated += Number(result && result.updated) || 0;
          alreadyKnown += Number(result && result.already_known) || 0;
          duplicatesIgnored += Number(result && result.duplicates_ignored) || 0;
          this.importProgress = i + batch.length;
        }
        this.importDuplicatesIgnored = duplicatesIgnored;
        this.importUpdated = updated;
        this.importAlreadyKnown = alreadyKnown;
        this.pagination.currentPage = 1;
        await this.loadContacts();
        this.showLaunchedModal = true;
      } catch (error) {
        let apiMessage = "";
        let status = null;
        if (typeof error === "string") {
          apiMessage = error;
        } else if (error && typeof error === "object") {
          apiMessage = error.message || "";
          status = error.status != null ? error.status : null;
        }
        const draftOnly = apiMessage === "Import allowed only for draft orders";
        const importedSoFar = imported + linked;
        const title = draftOnly
          ? this.$t("lang.orders.activation_import_draft_only_title")
          : this.$t("lang.orders.activation_import_error_title");
        let message;
        if (draftOnly) {
          message = this.$t("lang.orders.activation_import_draft_only_body");
        } else {
          const parts = [
            importedSoFar > 0
              ? this.$t("lang.orders.activation_import_error_partial", {
                  count: importedSoFar,
                })
              : this.$t("lang.orders.activation_import_error_body"),
          ];
          if (status) {
            parts.push(
              this.$t("lang.orders.activation_import_error_http", { status })
            );
          }
          message = parts.join(" ");
        }

        this.fileError = message;
        this.importDuplicatesIgnored = duplicatesIgnored;
        this.importUpdated = updated;
        this.importAlreadyKnown = alreadyKnown;
        if (importedSoFar > 0) {
          this.pagination.currentPage = 1;
          await this.loadContacts();
        }
        this.$swal({
          type: "error",
          title,
          text: message,
          showConfirmButton: true,
        });
      } finally {
        this.importing = false;
      }
    },
    loadContacts(options = {}) {
      if (!this.targetId) return Promise.resolve();
      const search = this.searchQuery.trim();
      const shouldFetchTotal =
        options.fetchTotal ||
        this.pagination.currentPage === 1 ||
        search !== this.lastSearchQuery;

      if (shouldFetchTotal) {
        this.loadContactsTotal();
      }

      this.loadingContacts = true;
      return targetsService
        .getActivationContacts(this.targetId, {
          currentPage: this.pagination.currentPage,
          cantPerPage: this.pagination.cantPerPage,
          search,
          getTotal: 0,
        })
        .then((response) => {
          if (response && Array.isArray(response.list)) {
            this.contacts = response.list.map((item) => {
              const contact = {
                id: item.id,
                first_name: item.first_name || "",
                last_name: item.last_name || "",
                domain: item.domain || "",
                job: item.job || item.post || "",
                company: item.company || item.business_name || "",
                linkedin: item.linkedin || item.linkedinurl || "",
                email: item.email || "",
                phone: item.phone || "",
                business_contact_status_id: item.business_contact_status_id,
              };
              this.$set(this.selectedContactsById, contact.id, contact);
              return contact;
            });
            if (
              this.contacts.length === 0 &&
              this.pagination.currentPage > 1 &&
              this.total > 0
            ) {
              this.pagination.currentPage = Math.max(
                1,
                Math.ceil(this.total / this.pagination.cantPerPage)
              );
              return this.loadContacts();
            }
          } else {
            this.contacts = [];
            if (shouldFetchTotal) {
              this.total = 0;
              this.$emit("contacts-changed", {
                total: 0,
                with_email: 0,
                without_email: 0,
              });
            }
          }
        })
        .catch(() => {
          this.contacts = [];
          if (shouldFetchTotal) {
            this.total = 0;
            this.withEmailCount = 0;
            this.withoutEmailCount = 0;
            this.emitContactsChanged();
          }
        })
        .finally(() => {
          this.loadingContacts = false;
        });
    },
    loadContactsTotal() {
      if (!this.targetId) return Promise.resolve();
      return targetsService
        .getActivationContactsStats(this.targetId)
        .then((response) => {
          this.total = Number(response.total) || 0;
          this.withEmailCount = Number(response.with_email) || 0;
          this.withoutEmailCount = Number(response.without_email) || 0;
          if (this.total === 0) {
            this.importFileName = "";
          }
          this.emitContactsChanged();
        })
        .catch(() => {
          this.total = 0;
          this.withEmailCount = 0;
          this.withoutEmailCount = 0;
          this.emitContactsChanged();
        });
    },
    reloadList() {
      this.clearSelection();
      this.pagination.currentPage = 1;
      return this.loadContacts({ fetchTotal: true });
    },
    onSearchChange() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.pagination.currentPage = 1;
      this.searchDebounceTimer = setTimeout(() => {
        this.loadContacts();
      }, 400);
    },
    toggleOne(id) {
      if (this.selectAllPages) {
        if (this.excludedIds.includes(id)) {
          this.excludedIds = this.excludedIds.filter((item) => item !== id);
        } else {
          this.excludedIds = [...this.excludedIds, id];
        }
        return;
      }
      if (this.selectedIds.includes(id)) {
        this.selectedIds = this.selectedIds.filter((item) => item !== id);
      } else {
        this.selectedIds = [...this.selectedIds, id];
        const contact = this.contacts.find((item) => item.id === id);
        if (contact) {
          this.$set(this.selectedContactsById, id, contact);
        }
      }
    },
    toggleAllPage(event) {
      if (event.target.checked) {
        const merged = new Set([...this.selectedIds, ...this.pageContactIds]);
        this.selectedIds = Array.from(merged);
        this.contacts.forEach((contact) => {
          this.$set(this.selectedContactsById, contact.id, contact);
        });
      } else {
        this.selectedIds = this.selectedIds.filter((id) => !this.pageContactIds.includes(id));
      }
    },
    openEnrichModal() {
      this.actionsOpen = false;
      if (this.sequenceActive) {
        this.showLockedModal = true;
        return;
      }
      if (!this.hasSelection || this.disabled) return;
      this.enrichIncludePhone = false;
      this.showEnrichModal = true;
    },
    closeEnrichModal() {
      this.showEnrichModal = false;
      this.enriching = false;
    },
    confirmEnrich() {
      if (!this.hasSelection || !this.targetId) return;
      this.enriching = true;
      targetsService
        .enrichActivationContacts(this.targetId, {
          selectAll: this.selectAllPages,
          selectedIds: this.selectAllPages ? [] : this.selectedIds,
          excludedIds: this.excludedIds,
          enrichPhone: this.enrichIncludePhone,
        })
        .then((response) => {
          this.beginEnrichmentTracking(response);
          this.closeEnrichModal();
          this.clearSelection();
          return this.reloadList().then(() => response);
        })
        .then((response) => {
          const queued = response && Number(response.queued) > 0
            ? Number(response.queued)
            : 0;
          if (queued > 0) {
            this.$swal({
              type: "success",
              title: this.$t("lang.orders.activation_import_enrich_queued_title"),
              text: this.$t("lang.orders.activation_import_enrich_queued_body", {
                count: queued,
              }),
              showConfirmButton: true,
            });
          }
        })
        .catch((error) => {
          const message = error && error.message ? error.message : "";
          if (message === "no-enrichable-contacts") {
            this.$swal({
              type: "info",
              title: this.$t("lang.orders.activation_import_enrich_none_title"),
              text: this.$t("lang.orders.activation_import_enrich_none_body"),
              showConfirmButton: true,
            });
            return;
          }
          this.$swal({
            type: "error",
            title: this.$t("lang.orders.activation_import_parse_error"),
            showConfirmButton: true,
          });
        })
        .finally(() => {
          this.enriching = false;
        });
    },
    csvEscape(value) {
      const text = value == null ? "" : String(value);
      if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    },
    async resolveSelectedContacts() {
      const byId = { ...this.selectedContactsById };
      this.contacts.forEach((contact) => {
        byId[contact.id] = contact;
      });

      const missing = this.selectedIds.filter((id) => !byId[id]);
      if (!missing.length || !this.targetId) {
        return this.selectedIds.map((id) => byId[id]).filter(Boolean);
      }

      const cantPerPage = 500;
      let page = 1;
      let fetched = 0;
      const maxPages = 20;

      while (missing.some((id) => !byId[id]) && page <= maxPages) {
        const response = await targetsService.getActivationContacts(this.targetId, {
          currentPage: page,
          cantPerPage,
          search: "",
          getTotal: 0,
        });
        const list = response && Array.isArray(response.list) ? response.list : [];
        if (!list.length) break;

        list.forEach((item) => {
          const contact = {
            id: item.id,
            first_name: item.first_name || "",
            last_name: item.last_name || "",
            domain: item.domain || "",
            job: item.job || item.post || "",
            company: item.company || item.business_name || "",
            linkedin: item.linkedin || item.linkedinurl || "",
            email: item.email || "",
            phone: item.phone || "",
            business_contact_status_id: item.business_contact_status_id,
          };
          byId[contact.id] = contact;
          this.$set(this.selectedContactsById, contact.id, contact);
        });

        fetched += list.length;
        if (list.length < cantPerPage) break;
        page += 1;
        if (fetched > 10000) break;
      }

      return this.selectedIds.map((id) => byId[id]).filter(Boolean);
    },
    async downloadSelection() {
      this.actionsOpen = false;
      if (!this.hasSelection) return;

      if (this.selectAllPages) {
        await this.exportSelectionViaQueue();
        return;
      }

      await this.downloadSelectionCsv();
    },
    async exportSelectionViaQueue() {
      try {
        const result = await this.$swal({
          type: "warning",
          title: this.$t("lang.mycontacts.shippingNoticeToEmail"),
          text: this.$t("lang.mycontacts.exportwhy"),
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33a2c",
          confirmButtonText: this.$t("lang.button.yes"),
          cancelButtonText: this.$t("lang.button.cancel"),
        });
        if (!result || !result.value) return;

        await targetsService.exportActivationContacts(this.targetId, {
          selectAll: true,
          excludedIds: this.excludedIds,
          search: this.searchQuery.trim(),
        });

        this.$swal({
          type: "success",
          title: this.$t("lang.mycontacts.shippingNoticeToEmail"),
          showConfirmButton: false,
          timer: 2500,
        });
      } catch (error) {
        this.$swal({
          type: "error",
          title: this.$t("lang.orders.activation_import_parse_error"),
          showConfirmButton: true,
        });
      }
    },
    async downloadSelectionCsv() {
      try {
        const contacts = await this.resolveSelectedContacts();
        const headers = [
          "first_name",
          "last_name",
          "domain",
          "job_title",
          "company_name",
          "linkedin_url",
          "email",
          "phone",
        ];
        const lines = [headers.join(",")];
        contacts.forEach((contact) => {
          lines.push(
            [
              contact.first_name,
              contact.last_name,
              contact.domain,
              contact.job,
              contact.company,
              contact.linkedin,
              contact.email,
              contact.phone,
            ]
              .map((value) => this.csvEscape(value))
              .join(",")
          );
        });

        const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const stamp = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.download = `activation-contacts-${stamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        // Keep silent for now; download failure is rare and non-blocking.
      }
    },
    askDeleteOne(id) {
      if (this.sequenceActive) {
        this.showLockedModal = true;
        return;
      }
      this.pendingDeleteIds = [id];
      this.showConfirmModal = true;
    },
    askDeleteSelected() {
      this.actionsOpen = false;
      if (this.sequenceActive) {
        this.showLockedModal = true;
        return;
      }
      if (!this.hasSelection) return;
      this.pendingDeleteIds = this.selectAllPages
        ? this.contacts.filter((c) => this.isRowSelected(c.id)).map((c) => c.id)
        : [...this.selectedIds];
      this.showConfirmModal = true;
    },
    confirmDelete() {
      const ids = [...this.pendingDeleteIds];
      this.pendingDeleteIds = [];
      this.showConfirmModal = false;
      if (!this.targetId || !ids.length) return;
      targetsService
        .deleteActivationContacts(this.targetId, { selectedIds: ids })
        .then(() => {
          this.selectedIds = this.selectedIds.filter((id) => !ids.includes(id));
          if (this.contacts.length === ids.length && this.pagination.currentPage > 1) {
            this.pagination.currentPage -= 1;
          }
          return this.loadContacts({ fetchTotal: true });
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.target-contact-import {
  padding: 0.5rem 0;
}

.list-loading {
  min-height: 12rem;
}

.empty-wrap {
  background: #fafbff;
}

.empty-sub {
  max-width: 360px;
}

.empty-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #ececfd;
  color: #4b4be1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}

.import-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #eef4ff;
  border: 1px solid #cfe0ff;
  border-radius: 8px;
  padding: 12px 16px;
}

.readonly-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  background: #fff8eb;
  color: #7a5b16;
}

.readonly-banner i {
  margin-top: 2px;
}

.import-banner__spin {
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(75, 75, 225, 0.25);
  border-top-color: #4b4be1;
  border-radius: 50%;
  animation: tci-spin 0.7s linear infinite;
  flex-shrink: 0;
}

.import-banner__count {
  margin-left: auto;
  font-weight: 700;
  color: #4b4be1;
  white-space: nowrap;
}

.enrichment-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid;
  border-radius: 8px;
}

.enrichment-banner--processing {
  color: #34349f;
  background: #eef4ff;
  border-color: #cfe0ff;
}

.enrichment-banner--retry,
.enrichment-banner--expired {
  color: #7a5b16;
  background: #fff8eb;
  border-color: #f0d9a8;
}

.enrichment-banner--completed {
  color: #187451;
  background: #eaf8f2;
  border-color: #bfe5d4;
}

.enrichment-banner--failed {
  color: #a72e38;
  background: #fdf2f3;
  border-color: #efc5c9;
}

.enrichment-banner__spin,
.field-enrichment__spin {
  border-style: solid;
  border-color: currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: tci-spin 0.7s linear infinite;
  flex-shrink: 0;
}

.enrichment-banner__spin {
  width: 18px;
  height: 18px;
  border-width: 2.5px;
}

.enrichment-banner__icon {
  font-size: 18px;
  flex-shrink: 0;
}

.enrichment-banner__content {
  min-width: 0;
  flex: 1;
}

.enrichment-banner__progress {
  width: 100%;
  max-width: 420px;
  height: 4px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(75, 75, 225, 0.15);
}

.enrichment-banner__progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: currentColor;
  transition: width 0.25s ease;
}

.enrichment-results {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.enrichment-result {
  min-width: 120px;
  padding: 8px 10px;
  border: 1px solid rgba(24, 116, 81, 0.18);
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.65);
}

.enrichment-result strong,
.enrichment-result span {
  display: block;
}

.enrichment-result strong {
  font-size: 16px;
  line-height: 1.1;
}

.enrichment-result span {
  margin-top: 3px;
  font-size: 11px;
}

.enrichment-result--removed {
  color: #7a5b16;
  border-color: rgba(122, 91, 22, 0.2);
}

.enrichment-banner__count {
  font-weight: 700;
  white-space: nowrap;
}

.enrichment-banner__close {
  padding: 2px 4px;
  color: inherit;
  background: transparent;
}

.field-enrichment {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.field-enrichment--active {
  color: #4b4be1;
  font-weight: 600;
}

.field-enrichment--failed {
  color: #d9434f;
}

.field-enrichment--empty {
  color: #9698ad;
}

.field-enrichment__spin {
  width: 12px;
  height: 12px;
  border-width: 2px;
}

.filename {
  font-size: 12px;
  padding: 6px 10px;
}

.search-box {
  position: relative;
  min-width: 240px;
  border: 1px solid #e9eaf1;
  border-radius: 7px;
  background: #fff;
}

.search-box:focus-within {
  border-color: #4b4be1;
}

.search-box i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9698ad;
  z-index: 2;
}

.search-box input {
  padding-left: 30px;
  border: none;
  background: transparent;
}

.search-box input:focus {
  box-shadow: none;
}

.activation-table tbody tr.table-primary {
  background: #ececfd;
}

.warn-icon {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #fdf2f3;
  color: #d9434f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.warn-icon--danger {
  background: #fdf2f3;
  color: #d9434f;
}

.selection-panel {
  position: relative;
}

.selection-toggle {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #fff;
}

.selection-toggle--active {
  background: #ececfd;
  border-color: #4b4be1 !important;
}

.selection-panel .dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 220px;
  margin: 0;
  padding: 0.35rem 0;
  display: none;
  border: 1px solid #e9eaf1;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(20, 22, 40, 0.12);
}

.selection-panel .dropdown-menu.show {
  display: block;
}

.selection-panel .dropdown-item {
  font-size: 13px;
  white-space: nowrap;
}

.list-toolbar {
  position: relative;
  z-index: 2;
}

.actions-panel {
  position: relative;
}

.actions-panel .dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: auto;
  z-index: 20;
  min-width: 220px;
  margin: 0;
  padding: 0.35rem 0;
  float: none;
  display: none;
  border: 1px solid #e9eaf1;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(20, 22, 40, 0.12);
}

.actions-panel .dropdown-menu.show {
  display: block;
}

.actions-panel .dropdown-header {
  white-space: nowrap;
  font-size: 11px;
}

.actions-panel .dropdown-item {
  font-size: 13px;
  white-space: nowrap;
}

.actions-panel .dropdown-item:disabled {
  opacity: 0.45;
  pointer-events: none;
}

.actions-panel.open .dropdown-toggle::after {
  transform: rotate(180deg);
}

.activation-table th,
.activation-table td {
  white-space: nowrap;
  vertical-align: middle;
  font-size: 12px;
}

.col-check {
  width: 38px;
  text-align: center;
}

.col-act {
  width: 56px;
}

.linkedin-cell {
  min-width: 56px;
}

.linkedin-indicator {
  display: inline-block;
  font-size: 14px;
  line-height: 1;
  vertical-align: middle;
}

.empty-cell {
  color: #bfc1d4;
}

.tci-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 22, 40, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 20px;
}

.tci-modal {
  width: 100%;
  max-width: 620px;
  max-height: 88vh;
  overflow: auto;
}

.tci-modal--sm {
  max-width: 430px;
}

.steps .step {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #9698ad;
  white-space: nowrap;
}

.preview-mapping {
  background: #fafbff;
  border: 1px solid #e9eaf1;
}

.preview-mapping-label {
  min-width: 120px;
}

.preview-table-wrap {
  max-height: 220px;
}

.preview-stats .badge-success {
  background: #e7f6ef;
  color: #1f9d6b;
}

.preview-stats .badge-warning {
  background: #fff8e6;
  color: #c9821a;
}

.step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f1f2f7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}

.step--active {
  color: #4b4be1;
}

.step--active .step-num {
  background: #4b4be1;
  color: #fff;
}

.step--done {
  color: #1f9d6b;
}

.step--done .step-num {
  background: #1f9d6b;
  color: #fff;
}

.step-sep {
  height: 1px;
  background: #e9eaf1;
}

.dropzone {
  border: 1.6px dashed #c9caea;
  border-radius: 9px;
  padding: 38px 20px;
  text-align: center;
  cursor: pointer;
  background: #fafbff;
  transition: 0.15s;
}

.dropzone:hover,
.dropzone--active {
  border-color: #4b4be1;
  background: #ececfd;
}

.dropzone-icon {
  width: 40px;
  height: 40px;
  margin: 0 auto 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e9eaf1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b4be1;
}

.file-picked {
  background: #e7f6ef;
  border: 1px solid #cfe0d8;
}

.launch-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #ececfd;
  color: #4b4be1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
}

.enrich-option label {
  cursor: pointer;
  font-size: 13px;
}

.enrich-option input[disabled] {
  cursor: not-allowed;
}

@keyframes tci-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
